import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Network, Plus, Trash2, Edit2, Upload, Mail, Phone, Loader2, X, ChevronDown, ChevronUp, GitBranch } from 'lucide-react';
import { toast } from 'sonner';
import TeamKernelNetwork from '../components/TeamKernelNetwork';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LAYER_COLORS = [
  'bg-gradient-to-r from-violet-600 to-purple-600 text-white',
  'bg-gradient-to-r from-blue-600 to-cyan-600 text-white',
  'bg-gradient-to-r from-emerald-600 to-teal-600 text-white',
  'bg-gradient-to-r from-amber-500 to-orange-500 text-white',
  'bg-gradient-to-r from-rose-500 to-pink-500 text-white',
];

const TeamKernelPage = () => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'hr'].includes(user?.role);
  const [kernels, setKernels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editKernel, setEditKernel] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newKernel, setNewKernel] = useState({ name: '', description: '' });
  const [expandedKernel, setExpandedKernel] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [viewMode, setViewMode] = useState('network'); // 'network' (default) | 'layered'

  const fetchKernels = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/team-kernels`, { withCredentials: true });
      setKernels(res.data);
      if (res.data.length > 0 && !expandedKernel) setExpandedKernel(res.data[0].id);
    } catch (err) {
      console.error('Failed to fetch kernels:', err);
    } finally {
      setLoading(false);
    }
  }, [expandedKernel]);

  useEffect(() => { fetchKernels(); }, [fetchKernels]);

  const handleCreate = async () => {
    if (!newKernel.name.trim()) return;
    try {
      await axios.post(`${API_URL}/api/team-kernels`, {
        ...newKernel,
        layers: [
          { title: 'Founders', members: [] },
          { title: 'AI Architect', members: [] },
          { title: 'AI Team Lead', members: [] },
          { title: 'AI Engineer', members: [] },
          { title: 'Associate AI Engineer', members: [] },
        ]
      }, { withCredentials: true });
      toast.success('Team Kernel created');
      setIsCreateOpen(false);
      setNewKernel({ name: '', description: '' });
      fetchKernels();
    } catch (err) {
      toast.error('Failed to create kernel');
    }
  };

  const handleSaveKernel = async (kernel) => {
    try {
      await axios.put(`${API_URL}/api/team-kernels/${kernel.id}`, {
        name: kernel.name, description: kernel.description, layers: kernel.layers,
      }, { withCredentials: true });
      toast.success('Team Kernel saved');
      setEditKernel(null);
      fetchKernels();
    } catch (err) {
      toast.error('Failed to save kernel');
    }
  };

  const handleDeleteKernel = async (kernelId) => {
    if (!window.confirm('Delete this team kernel?')) return;
    try {
      await axios.delete(`${API_URL}/api/team-kernels/${kernelId}`, { withCredentials: true });
      toast.success('Kernel deleted');
      fetchKernels();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleUploadPhoto = async (file, memberName) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/api/team-kernels/upload-photo?member_name=${encodeURIComponent(memberName)}`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data.path;
    } catch (err) {
      toast.error('Failed to upload photo');
      return null;
    } finally {
      setUploading(false);
    }
  };

  // Editing helpers
  const addMemberToLayer = (layerIdx) => {
    if (!editKernel) return;
    const layers = [...editKernel.layers];
    layers[layerIdx].members.push({ name: '', email: '', phone: '', photo: '' });
    setEditKernel({ ...editKernel, layers });
  };

  const updateMember = (layerIdx, memberIdx, field, value) => {
    if (!editKernel) return;
    const layers = [...editKernel.layers];
    layers[layerIdx].members[memberIdx][field] = value;
    setEditKernel({ ...editKernel, layers });
  };

  const removeMember = (layerIdx, memberIdx) => {
    if (!editKernel) return;
    const layers = [...editKernel.layers];
    layers[layerIdx].members.splice(memberIdx, 1);
    setEditKernel({ ...editKernel, layers });
  };

  const addLayer = () => {
    if (!editKernel) return;
    const layers = [...editKernel.layers, { title: 'New Layer', members: [] }];
    setEditKernel({ ...editKernel, layers });
  };

  const updateLayerTitle = (layerIdx, title) => {
    if (!editKernel) return;
    const layers = [...editKernel.layers];
    layers[layerIdx].title = title;
    setEditKernel({ ...editKernel, layers });
  };

  const removeLayer = (layerIdx) => {
    if (!editKernel) return;
    const layers = [...editKernel.layers];
    layers.splice(layerIdx, 1);
    setEditKernel({ ...editKernel, layers });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="team-kernel-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Network className="w-7 h-7 text-primary" />Team Kernel</h1>
          <p className="text-muted-foreground">Wisewit's flat hierarchy — every team member is directly linked to the founders.</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="inline-flex bg-muted rounded-md p-0.5 text-xs">
            <button
              onClick={() => setViewMode('network')}
              className={`px-3 py-1.5 rounded-md font-medium transition flex items-center gap-1.5 ${viewMode === 'network' ? 'bg-background shadow-sm' : 'text-muted-foreground'}`}
              data-testid="view-mode-network"
            >
              <Network className="w-3.5 h-3.5" /> Network
            </button>
            <button
              onClick={() => setViewMode('layered')}
              className={`px-3 py-1.5 rounded-md font-medium transition flex items-center gap-1.5 ${viewMode === 'layered' ? 'bg-background shadow-sm' : 'text-muted-foreground'}`}
              data-testid="view-mode-layered"
            >
              <GitBranch className="w-3.5 h-3.5" /> Layered
            </button>
          </div>
          {isAdmin && (
            <Button onClick={() => setIsCreateOpen(true)} className="gap-1.5" data-testid="create-kernel-btn">
              <Plus className="w-4 h-4" /> New Kernel
            </Button>
          )}
        </div>
      </div>

      {kernels.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-muted-foreground">
          <Network className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No team kernels created yet.{isAdmin && ' Click "New Kernel" to get started.'}</p>
        </CardContent></Card>
      ) : (
        kernels.map((kernel) => (
          <Card key={kernel.id} data-testid={`kernel-${kernel.id}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="cursor-pointer flex-1" onClick={() => setExpandedKernel(expandedKernel === kernel.id ? null : kernel.id)}>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Network className="w-5 h-5 text-primary" />
                    {kernel.name}
                    {expandedKernel === kernel.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </CardTitle>
                  {kernel.description && <CardDescription className="mt-1">{kernel.description}</CardDescription>}
                </div>
                {isAdmin && (
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditKernel(JSON.parse(JSON.stringify(kernel)))} data-testid={`edit-kernel-${kernel.id}`}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => handleDeleteKernel(kernel.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            {expandedKernel === kernel.id && (
              <CardContent>
                {viewMode === 'network' ? (
                  <TeamKernelNetwork kernel={kernel} />
                ) : (
                  <div className="space-y-6">
                    {(kernel.layers || []).map((layer, layerIdx) => (
                      <div key={layerIdx} className="relative">
                        {layerIdx > 0 && (
                          <div className="absolute left-1/2 -top-3 w-0.5 h-3 bg-border" />
                        )}
                        <div className={`rounded-lg p-4 ${LAYER_COLORS[layerIdx % LAYER_COLORS.length]}`}>
                          <h3 className="font-semibold text-center mb-3 text-sm uppercase tracking-wider">{layer.title}</h3>
                          {layer.members.length === 0 ? (
                            <p className="text-center text-sm opacity-70">No members added</p>
                          ) : (
                            <div className="flex flex-wrap justify-center gap-4">
                              {layer.members.map((member, mIdx) => (
                                <div key={mIdx} className="bg-white/20 backdrop-blur-sm rounded-lg p-3 min-w-[200px] max-w-[240px]" data-testid={`member-card-${layerIdx}-${mIdx}`}>
                                  <div className="flex items-center gap-3">
                                    {member.photo ? (
                                      <img src={`${API_URL}/api/files/${member.photo}`} alt={member.name} className="w-10 h-10 rounded-full object-cover ring-2 ring-white/50" />
                                    ) : (
                                      <div className="w-10 h-10 rounded-full bg-white/30 flex items-center justify-center text-sm font-bold">
                                        {(member.name || '?').charAt(0).toUpperCase()}
                                      </div>
                                    )}
                                    <div className="min-w-0">
                                      <p className="font-semibold text-sm truncate">{member.name || 'Unnamed'}</p>
                                      {member.designation && <p className="text-[10px] opacity-80 truncate">{member.designation}</p>}
                                      {member.email && <p className="text-xs opacity-80 truncate flex items-center gap-1"><Mail className="w-3 h-3" />{member.email}</p>}
                                      {member.phone && <p className="text-xs opacity-80 flex items-center gap-1"><Phone className="w-3 h-3" />{member.phone}</p>}
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {member.employee_id && (
                                          <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-white/25">{member.employee_id}</span>
                                        )}
                                        {member.employment_type && member.employment_type !== 'full_time' && (
                                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/40 border border-amber-200/50" title={member.contract_end_date ? `Ends ${member.contract_end_date}` : ''}>
                                            {({ part_time: 'Part-Time', contract: 'Contract', intern: 'Intern', fixed_term: 'Fixed-Term' })[member.employment_type] || member.employment_type}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        ))
      )}

      {/* Create Kernel Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Team Kernel</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Name</Label><Input value={newKernel.name} onChange={(e) => setNewKernel({ ...newKernel, name: e.target.value })} placeholder="e.g., AI Team" data-testid="kernel-name" /></div>
            <div className="space-y-2"><Label>Description</Label><Textarea value={newKernel.description} onChange={(e) => setNewKernel({ ...newKernel, description: e.target.value })} placeholder="Team description..." rows={2} /></div>
            <p className="text-xs text-muted-foreground">Default layers: Founders → AI Architect → AI Team Lead → AI Engineer → Associate AI Engineer</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={!newKernel.name.trim()} data-testid="create-kernel-submit">Create</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Kernel Dialog */}
      <Dialog open={!!editKernel} onOpenChange={(open) => !open && setEditKernel(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Team Kernel: {editKernel?.name}</DialogTitle></DialogHeader>
          {editKernel && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Name</Label><Input value={editKernel.name} onChange={(e) => setEditKernel({ ...editKernel, name: e.target.value })} /></div>
                <div className="space-y-2"><Label>Description</Label><Input value={editKernel.description || ''} onChange={(e) => setEditKernel({ ...editKernel, description: e.target.value })} /></div>
              </div>

              {editKernel.layers.map((layer, layerIdx) => (
                <div key={layerIdx} className="border rounded-lg p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <Input value={layer.title} onChange={(e) => updateLayerTitle(layerIdx, e.target.value)} className="font-semibold h-8 text-sm w-48" />
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => addMemberToLayer(layerIdx)}><Plus className="w-3 h-3" />Add Person</Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeLayer(layerIdx)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </div>
                  {layer.members.map((member, mIdx) => (
                    <div key={mIdx} className="grid grid-cols-[auto_1fr_1fr_1fr_auto] gap-2 items-center bg-muted/30 p-2 rounded">
                      <label className="cursor-pointer">
                        {member.photo ? (
                          <img src={`${API_URL}/api/files/${member.photo}`} alt="" className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center"><Upload className="w-3 h-3 text-muted-foreground" /></div>
                        )}
                        <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                          if (e.target.files[0]) {
                            const path = await handleUploadPhoto(e.target.files[0], member.name || 'member');
                            if (path) updateMember(layerIdx, mIdx, 'photo', path);
                          }
                        }} />
                      </label>
                      <Input value={member.name} onChange={(e) => updateMember(layerIdx, mIdx, 'name', e.target.value)} placeholder="Name" className="h-8 text-sm" />
                      <Input value={member.email || ''} onChange={(e) => updateMember(layerIdx, mIdx, 'email', e.target.value)} placeholder="Email" className="h-8 text-sm" />
                      <Input value={member.phone || ''} onChange={(e) => updateMember(layerIdx, mIdx, 'phone', e.target.value)} placeholder="Phone" className="h-8 text-sm" />
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeMember(layerIdx, mIdx)}><X className="w-3 h-3" /></Button>
                    </div>
                  ))}
                </div>
              ))}

              <Button variant="outline" className="w-full gap-1" onClick={addLayer}><Plus className="w-4 h-4" />Add Layer</Button>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditKernel(null)}>Cancel</Button>
                <Button onClick={() => handleSaveKernel(editKernel)} data-testid="save-kernel-btn">Save Kernel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamKernelPage;
